// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ExtractedAnnualStatement, ExtractedFundRow } from '../types/extraction';
import type { LoadedPdf, PdfTextItem } from './pdfTextLoader';
import { fullText, groupItemsByRow } from './pdfTextLoader';

/**
 * Extracts policy fields from a Standard Life Synergy PRSA annual statement.
 *
 * The strategy is anchor + regex against the joined page text. The PDF
 * uses fairly stable English labels (e.g. `Policy number:`, `Date of birth`,
 * `Policy value on <date>`), so a small set of patterns is enough for the
 * fields the form needs. Anything we can't match with confidence is left
 * `undefined` so the user can fill it manually.
 */
export function extractAnnualStatement(pdf: LoadedPdf): ExtractedAnnualStatement {
  const text = normaliseWhitespace(fullText(pdf));
  const out: ExtractedAnnualStatement = {};

  // ---------- Policy number / category / type ----------
  // Require a real policy-number shape: letters then digits (e.g. PR100012701).
  // Scan all occurrences of "Policy number …" and pick the first that matches.
  const policyNumberMatches = text.matchAll(
    /Policy number[:\s]+([A-Z]{1,4}\d[A-Z0-9-]*)/gi,
  );
  for (const m of policyNumberMatches) {
    if (m[1]) {
      out.fullPolicyNumber = m[1];
      break;
    }
  }

  if (/personal retirement savings account/i.test(text) || /\bPRSA\b/.test(text)) {
    out.policyCategory = 'Pension';
    out.pensionType = 'PRSA';
    out.policyType = 'PRSA_STANDARD';
  }

  // No closure narrative ⇒ assume active.
  if (!/policy.*(closed|surrendered|transferred out)/i.test(text)) {
    out.policyStatus = 'ACTIVE';
  }

  // ---------- Provider / completion date ----------
  out.providerContactName = 'Standard Life';

  const issueDate = matchOne(
    text,
    /Statement (?:of|issued|issue date)[:\s]+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i,
  );
  const issueDateAlt = matchOne(text, /issued[\s:]+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i);
  // Fallback: extract the statement end date from "Statement from <date> to <date>"
  const statementEndDate = matchOne(
    text,
    /Statement from \d{1,2}\s+[A-Za-z]+\s+\d{4}\s+to\s+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i,
  );
  const isoIssue = parseLongDate(issueDate || issueDateAlt || statementEndDate);
  if (isoIssue) out.completionDate = isoIssue;

  // ---------- Policyholder ----------
  // The address block sits beneath `Policy holder:` on page 1; pdfjs
  // serialises it as a sequence of lines. Stop at the next label token
  // (`Policy`, `Date`, `Retirement`) so the capture doesn't bleed into
  // the rest of the header.
  const holderLine = matchOne(
    text,
    /Policy holder[:\s]+([A-Z][A-Za-z'’\- ]+?)\s+(?:Policy|Date|Retirement|Statement)/,
  );
  if (holderLine) out.policyholderName = holderLine.trim();

  const dob = matchOne(text, /Date of birth[:\s]+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i);
  const isoDob = parseLongDate(dob);
  if (isoDob) out.policyholderDateOfBirth = isoDob;

  // Address: walk the left-column items on page 1 upward from the country.
  const address = extractAddressBlock(pdf);
  if (address) {
    out.policyholderAddressLine1 = address.line1;
    if (address.line2) out.policyholderAddressLine2 = address.line2;
    if (address.line3) out.policyholderAddressLine3 = address.line3;
    if (address.city) out.policyholderCity = address.city;
    if (address.postalCode) out.policyholderPostalCode = address.postalCode;
    if (address.countryCode) out.policyholderCountryCode = address.countryCode;
  }

  // ---------- Policy dates ----------
  const startDate = matchOne(text, /Policy start date[:\s]+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i);
  const isoStart = parseLongDate(startDate);
  if (isoStart) out.policyStartDate = isoStart;

  // ---------- Valuations ----------
  // `Policy value on 12 May 2025 €123,456.78`
  const policyValue = matchPair(
    text,
    /Policy value on (\d{1,2}\s+[A-Za-z]+\s+\d{4})\s*€?\s*([\d.,]+)/i,
  );
  if (policyValue) {
    out.currentValueDate = parseLongDate(policyValue[0]) ?? undefined;
    out.currentValue = normaliseCurrency(policyValue[1]);
  }

  const transferValue = matchPair(
    text,
    /Transfer value on (\d{1,2}\s+[A-Za-z]+\s+\d{4})\s*€?\s*([\d.,]+)/i,
  );
  if (transferValue) {
    out.transferValueDate = parseLongDate(transferValue[0]) ?? undefined;
    out.transferValue = normaliseCurrency(transferValue[1]);
  }

  // ---------- Contributions / transfers in ----------
  // Only use the "since your policy started" running totals — the
  // statement-period rows ("since <statement start date>") reset each
  // period and don't represent lifetime contributions.
  const employeeTotal = matchOne(
    text,
    /Total employee contributions since (?:your )?policy started\s*€?\s*([\d.,]+)/i,
  );
  if (employeeTotal) out.employeeContribution = normaliseCurrency(employeeTotal);

  const employerTotal = matchOne(
    text,
    /Total employer contributions since (?:your )?policy started\s*€?\s*([\d.,]+)/i,
  );
  if (employerTotal) out.employerContribution = normaliseCurrency(employerTotal);

  const transfersIn = matchOne(
    text,
    /Total transfers? since (?:your )?policy started\s*€?\s*([\d.,]+)/i,
  );
  if (transfersIn) {
    out.transfersInFromAnotherScheme = normaliseCurrency(transfersIn);
    out.hasReceivedTransfers = parseFloat(transfersIn.replace(/,/g, '')) > 0 ? 'Yes' : 'No';
  }

  // Annual statement is for an active, contributing policy
  out.payingStatus = 'In Force';

  // ---------- Charges narrative (Section 3) ----------
  if (/no early surrender charges|no.*exit penalt/i.test(text)) {
    out.exitChargesDetails = 'No early surrender charges, transfer charges or exit penalties';
    out.transferOutCosts = 'No transfer charges or exit penalties';
  }
  if (/unit[- ]linked/i.test(text)) {
    out.guaranteedReturnsDetails = 'None — unit-linked PRSA';
    out.terminalBonusDetails = 'None — unit-linked PRSA';
  }
  if (/no service charge|no policy fee|no fund-based charge|no charge for switching/i.test(text)) {
    out.otherChargeDetails =
      'No service charge or policy fee, no charge for switching between funds, no fund-based charge';
  }

  // ---------- Section 2: Investment funds ----------
  const funds = extractInvestmentFunds(pdf);
  if (funds.length > 0) out.investmentFunds = funds;

  return out;
}

// ============================================================================
// Investment funds — positional table parser (Section 2 + Section 3 AMC join)
// ============================================================================

/**
 * Walks the PDF pages, finds the Section 2 fund table, and returns one
 * `ExtractedFundRow` per fund. Joins AMC from the Section 3 deductions
 * table by fund-name match (case-insensitive).
 *
 * The table on page 2/3 has the layout (approximate X ranges in PDF units):
 *
 *   Fund               % of policy   No. of units   Unit price   Value
 *   ~40 – ~260         ~270 – ~330   ~335 – ~410    ~415 – ~475  ~480+
 *
 * We don't hard-code those ranges; instead we locate the header row that
 * contains both `Fund` and `Value` and use its items' X positions as the
 * column anchors, snapping each subsequent row's numeric cells to the
 * nearest anchor. This survives small layout shifts across statement
 * versions.
 */
function extractInvestmentFunds(pdf: LoadedPdf): ExtractedFundRow[] {
  const funds: ExtractedFundRow[] = [];

  // Strategy: shape-based row matching. The annual statement's Section 2
  // has per-fund rows that always look like
  //   <fund name>  <% of policy>  <number of units>  <unit price cents>  €<value>
  // across 4 numeric cells. We don't rely on header detection (which
  // is brittle because the header spans multiple rows and uses words
  // like "Investment"/"Total"); instead we find rows that match the
  // shape on the `Investment information` page.
  for (const page of pdf.pages) {
    if (!/investment information/i.test(page.text)) continue;
    const rows = groupItemsByRow(page.items, 2.2);

    for (const row of rows) {
      const parsed = parseFundRowByShape(row);
      if (parsed) funds.push(parsed);
    }

    if (funds.length > 0) break;
  }

  // Join AMC percentages from the Section 3 deductions table
  if (funds.length > 0) {
    const amcByFund = extractAmcTable(pdf);
    for (const fund of funds) {
      const normalised = fund.fundName.trim().toLowerCase();
      const amc = amcByFund.get(normalised);
      if (amc !== undefined) fund.annualManagementCharge = amc;
    }
  }

  return funds;
}

/**
 * Identify a Section-2 fund row by shape alone:
 *   - first items (left, x < 260) form a fund name starting with a letter
 *   - exactly four numeric cells follow, ordered by X:
 *     percent, units, unit-price-cents, €-value
 */
function parseFundRowByShape(row: PdfTextItem[]): ExtractedFundRow | null {
  // Strip whitespace-only items
  const items = row.filter((i) => i.str.trim().length > 0);
  if (items.length < 5) return null;

  // Split into name items (non-numeric, left side) and numeric items.
  // Fund names can contain trailing integers (e.g. "SL Global Index 100"),
  // so a bare integer (no decimal point, no € prefix, no % suffix) is only
  // treated as numeric once a "real" numeric token (decimal, currency, or
  // percent) has already been seen.
  const nameItems: PdfTextItem[] = [];
  const numericItems: PdfTextItem[] = [];
  let seenNumeric = false;
  for (const item of items) {
    const s = item.str.trim();
    const hasDecimal = /^€?\d[\d,]*\.\d+%?$/.test(s);
    const hasCurrencyOrPercent = /^€\d/.test(s) || /\d%$/.test(s);
    const isBareInteger = /^\d[\d,]*$/.test(s);
    const isNumeric =
      hasDecimal || hasCurrencyOrPercent || (isBareInteger && seenNumeric);
    if (!isNumeric && !seenNumeric) {
      nameItems.push(item);
    } else {
      seenNumeric = true;
      if (hasDecimal || hasCurrencyOrPercent || isBareInteger) {
        numericItems.push(item);
      }
    }
  }

  if (nameItems.length === 0 || numericItems.length < 4) return null;
  const fundName = nameItems.map((i) => i.str).join(' ').replace(/\s+/g, ' ').trim();
  if (!/^[A-Z]/.test(fundName)) return null;

  // Fund name must not be a header/total row
  if (/^(total|investment|% of|number|unit price|amount|product|adviser)/i.test(fundName)) {
    return null;
  }

  // Sort numeric cells by X and map by position
  numericItems.sort((a, b) => a.x - b.x);
  const nums = numericItems.map((i) => parseFloat(i.str.replace(/[€,%]/g, '')));

  // A valid fund row: allocation% between 0 and 100, units > 0, unit price > 0, value > 0
  const [allocationPercent, units, unitPriceCents, value] = nums;
  if (
    Number.isNaN(allocationPercent) ||
    allocationPercent <= 0 ||
    allocationPercent > 100 ||
    Number.isNaN(value) ||
    value <= 0
  ) {
    return null;
  }

  return {
    fundName,
    allocationPercent,
    units: Number.isNaN(units) ? undefined : units,
    unitPriceCents: Number.isNaN(unitPriceCents) ? undefined : unitPriceCents,
    value,
  };
}

/**
 * Parses the Section 3 "Annual management charge" table to build a map
 * of `fund name → AMC %` (e.g. 1.05). Section 3 renders each fund as a
 * row with two numeric cells: AMC % and Additional fund expenses %. We
 * take the first (AMC) and drop the second.
 */
function extractAmcTable(pdf: LoadedPdf): Map<string, number> {
  const byFund = new Map<string, number>();

  for (const page of pdf.pages) {
    if (!/annual management charge/i.test(page.text)) continue;
    const rows = groupItemsByRow(page.items, 2.2);

    for (const row of rows) {
      // Filter out whitespace-only items
      const items = row.filter((i) => i.str.trim().length > 0);
      if (items.length < 2) continue;

      // Name items: leading non-percentage tokens; percentage items: the rest
      const nameItems: PdfTextItem[] = [];
      const percentItems: PdfTextItem[] = [];
      let seenPercent = false;
      for (const item of items) {
        const s = item.str.trim();
        const isPercent = /^\d[\d.,]*%$/.test(s);
        if (!isPercent && !seenPercent) {
          nameItems.push(item);
        } else {
          seenPercent = true;
          if (isPercent) percentItems.push(item);
        }
      }

      if (nameItems.length === 0 || percentItems.length < 1) continue;
      const fundName = nameItems.map((i) => i.str).join(' ').replace(/\s+/g, ' ').trim();
      if (!/^[A-Z]/.test(fundName)) continue;
      if (/^(investment|charge|additional|total)/i.test(fundName)) continue;

      // First percent item (smallest X) is the AMC
      percentItems.sort((a, b) => a.x - b.x);
      const amc = parseFloat(percentItems[0].str.replace('%', ''));
      if (!Number.isNaN(amc) && amc > 0 && amc < 10) {
        byFund.set(fundName.toLowerCase(), amc);
      }
    }
  }

  return byFund;
}

// ============================================================================
// Helpers
// ============================================================================

function normaliseWhitespace(s: string): string {
  return s.replace(/[\u00a0\t]/g, ' ').replace(/ +/g, ' ');
}

function matchOne(text: string, re: RegExp): string | undefined {
  const m = text.match(re);
  return m && m[1] ? m[1].trim() : undefined;
}

function matchPair(text: string, re: RegExp): [string, string] | undefined {
  const m = text.match(re);
  if (m && m[1] && m[2]) return [m[1].trim(), m[2].trim()];
  return undefined;
}

function normaliseCurrency(raw: string): string {
  // Wizard currency fields use a plain number string (no thousands separator).
  return raw.replace(/[€\s]/g, '').replace(/,/g, '');
}

const MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

function parseLongDate(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const m = input.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (!m) return undefined;
  const day = parseInt(m[1], 10);
  const month = MONTHS[m[2].toLowerCase()];
  const year = parseInt(m[3], 10);
  if (month === undefined) return undefined;
  const d = new Date(Date.UTC(year, month, day));
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().slice(0, 10);
}

interface ParsedAddress {
  line1: string;
  line2?: string;
  line3?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
}

/**
 * Locate the recipient address block on page 1 of the annual statement.
 *
 * Standard Life prints the block in the left column (x ≈ 90-220, above
 * the fold). We grab every non-empty item in that column, sort by Y
 * descending, find the country line, and walk upward until we hit the
 * name (Mr/Mrs/Ms/…) or run out of lines. The last address line before
 * the country typically carries `<city> <postcode>`.
 */
function extractAddressBlock(pdf: LoadedPdf): ParsedAddress | undefined {
  const page1 = pdf.pages[0];
  if (!page1) return undefined;

  const leftColumn = page1.items
    .filter((i) => i.x >= 90 && i.x <= 240 && i.str.trim().length > 0)
    .sort((a, b) => b.y - a.y);

  const countryIdx = leftColumn.findIndex((i) =>
    /^(republic of ireland|ireland|united kingdom|uk)$/i.test(i.str.trim()),
  );
  if (countryIdx < 0) return undefined;

  // Collect up to 4 address lines walking upward from the country line,
  // stopping at a title-prefixed name (Mr, Mrs, Ms…) or a non-address token.
  const addressLines: string[] = [];
  for (let i = countryIdx - 1; i >= 0 && addressLines.length < 4; i--) {
    const raw = leftColumn[i].str.trim();
    if (!raw) continue;
    if (/^(mr|mrs|ms|miss|dr|prof)\b\.?/i.test(raw)) break;
    // Skip incidental artefacts like "(EH547BB9U2)" or standalone digits
    if (/^\(.+\)$/.test(raw)) continue;
    if (/^\d{3,}$/.test(raw)) continue;
    addressLines.unshift(raw);
  }
  if (addressLines.length === 0) return undefined;

  // Extract postcode (Eircode or UK postcode) from the last address line
  const lastLine = addressLines[addressLines.length - 1];
  const eircode = lastLine.match(/\b([A-Z]\d{2}\s?[A-Z0-9]{4})\b/);
  const ukPostcode = lastLine.match(/\b([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b/);
  const postalCode = eircode?.[1] || ukPostcode?.[1];

  let city: string | undefined = lastLine;
  if (postalCode) city = lastLine.replace(postalCode, '').trim();

  const countryRaw = leftColumn[countryIdx].str.trim();
  const countryCode = /republic of ireland|^ireland$/i.test(countryRaw)
    ? 'IE'
    : /united kingdom|^uk$/i.test(countryRaw)
      ? 'GB'
      : undefined;

  // Build lines:
  //  - 2 address lines → line1 = addressLines[0], city = addressLines[1] (no postcode split)
  //  - 3 lines → line1, line2 = addressLines[1], city from last line
  //  - 4 lines → line1, line2, line3, city from last line
  const nonCityLines = addressLines.slice(0, -1);
  const line1 = nonCityLines[0];
  const line2 = nonCityLines[1];
  const line3 = nonCityLines[2];

  return {
    line1: line1 ?? addressLines[0],
    line2,
    line3,
    city,
    postalCode,
    countryCode,
  };
}
